import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon,
  BugReport as BugReportIcon,
  Construction as ConstructionIcon,
  ContentCopy as ContentCopyIcon,
  Explore as ExploreIcon,
  FolderCopy as FolderCopyIcon,
  Grain as GrainIcon,
  Home as HomeIcon,
  PlayArrow as PlayArrowIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import { Command } from "cmdk";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApiQuery } from "../hooks/use-api-query";
import { useDebounce } from "../hooks/use-debounce";
import type { Issue, Test, TestSuite } from "../types";
import { clickableProps } from "../utils/keyboard";
import "./CommandPalette.css";

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  shortcut?: string;
  group: string;
}

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300); // 300ms debounce
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Fetch data for dynamic search with query parameters (using debounced search)
  const { data: tests, isLoading: testsLoading } = useApiQuery<Test[]>(
    `/tests?q=${encodeURIComponent(debouncedSearch)}&limit=5`,
    {
      enabled: open && debouncedSearch.length > 1,
    },
  );

  const { data: suites, isLoading: suitesLoading } = useApiQuery<TestSuite[]>(
    `/test-suites?q=${encodeURIComponent(debouncedSearch)}`,
    {
      enabled: open && debouncedSearch.length > 1,
    },
  );

  const { data: issues, isLoading: issuesLoading } = useApiQuery<Issue[]>("/issues?limit=100", {
    enabled: open && debouncedSearch.length > 1,
  });

  // Keyboard listener for Cmd+K / Ctrl+K and ESC
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Handle ESC key to close the palette
      if (e.key === "Escape") {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        return;
      }

      // Prevent opening when user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      // Handle Cmd+K / Ctrl+K to toggle the palette
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open]);

  // Reset state when closing and auto-focus when opening
  useEffect(() => {
    if (!open) {
      setSearch("");
    } else {
      // Auto-focus the input when palette opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  const copyToClipboard = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        enqueueSnackbar(`${label} copied to clipboard`, {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
        });
        setOpen(false);
      } catch {
        enqueueSnackbar("Failed to copy to clipboard", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
        });
      }
    },
    [enqueueSnackbar],
  );

  const allNavigationCommands: CommandItem[] = useMemo(
    () => [
      {
        id: "nav-home",
        label: "Home",
        icon: <HomeIcon fontSize="small" />,
        action: () => {
          navigate("/");
          setOpen(false);
        },
        keywords: ["home", "dashboard"],
        group: "Navigation",
      },
      {
        id: "nav-tests",
        label: "Tests",
        icon: <ScienceIcon fontSize="small" />,
        action: () => {
          navigate("/test-cases");
          setOpen(false);
        },
        keywords: ["tests", "test cases"],
        group: "Navigation",
      },
      {
        id: "nav-suites",
        label: "Test Suites",
        icon: <FolderCopyIcon fontSize="small" />,
        action: () => {
          navigate("/tests-main/test-suites");
          setOpen(false);
        },
        keywords: ["suites", "test suites"],
        group: "Navigation",
      },
      {
        id: "nav-runs",
        label: "Test Runs",
        icon: <PlayArrowIcon fontSize="small" />,
        action: () => {
          navigate("/runs");
          setOpen(false);
        },
        keywords: ["runs", "test runs", "executions"],
        group: "Navigation",
      },
      {
        id: "nav-suite-runs",
        label: "Suite Runs",
        icon: <PlayArrowIcon fontSize="small" />,
        action: () => {
          navigate("/suite-runs");
          setOpen(false);
        },
        keywords: ["suite runs", "suite executions"],
        group: "Navigation",
      },
      {
        id: "nav-issues",
        label: "Issues",
        icon: <BugReportIcon fontSize="small" />,
        action: () => {
          navigate("/issues");
          setOpen(false);
        },
        keywords: ["issues", "bugs"],
        group: "Navigation",
      },
      {
        id: "nav-argus",
        label: "Argus",
        icon: <GrainIcon fontSize="small" />,
        action: () => {
          navigate("/argus");
          setOpen(false);
        },
        keywords: ["argus", "agent", "chat"],
        group: "Navigation",
      },
      {
        id: "nav-discovery",
        label: "Discovery Runs",
        icon: <ExploreIcon fontSize="small" />,
        action: () => {
          navigate("/discovery-runs");
          setOpen(false);
        },
        keywords: ["discovery", "discovery runs"],
        group: "Navigation",
      },
      {
        id: "nav-analytics",
        label: "Analytics",
        icon: <AssessmentIcon fontSize="small" />,
        action: () => {
          navigate("/analytics");
          setOpen(false);
        },
        keywords: ["analytics", "metrics", "stats"],
        group: "Navigation",
      },
      {
        id: "nav-coverage",
        label: "Coverage Graph",
        icon: <AccountTreeIcon fontSize="small" />,
        action: () => {
          navigate("/graph");
          setOpen(false);
        },
        keywords: ["coverage", "graph"],
        group: "Navigation",
      },
      {
        id: "nav-config",
        label: "Configuration",
        icon: <SettingsIcon fontSize="small" />,
        action: () => {
          navigate("/configuration");
          setOpen(false);
        },
        keywords: ["settings", "config", "configuration"],
        group: "Navigation",
      },
      {
        id: "nav-mcp",
        label: "MCP",
        icon: <ConstructionIcon fontSize="small" />,
        action: () => {
          navigate("/vibe-qa");
          setOpen(false);
        },
        keywords: ["mcp", "vibe qa"],
        group: "Navigation",
      },
    ],
    [navigate],
  );

  const allCreateCommands: CommandItem[] = useMemo(
    () => [
      {
        id: "create-test",
        label: "Create New Test",
        icon: <AddIcon fontSize="small" />,
        action: () => {
          navigate("/generate");
          setOpen(false);
        },
        keywords: ["create", "new", "test", "generate"],
        group: "Create",
      },
      {
        id: "create-discovery",
        label: "Create Discovery Run",
        icon: <ExploreIcon fontSize="small" />,
        action: () => {
          navigate("/discovery-runs?triggerOpen=true");
          setOpen(false);
        },
        keywords: ["create", "new", "discovery", "discover", "run"],
        group: "Create",
      },
    ],
    [navigate],
  );

  const allCopyCommands: CommandItem[] = useMemo(() => {
    const commands: CommandItem[] = [
      {
        id: "copy-url",
        label: "Copy Current URL",
        icon: <ContentCopyIcon fontSize="small" />,
        action: () => {
          const url = window.location.href;
          copyToClipboard(url, "URL");
        },
        keywords: ["copy", "url", "link"],
        group: "Copy",
      },
    ];

    // Add context-aware copy commands based on current route
    if (params.testId) {
      commands.push({
        id: "copy-test-id",
        label: "Copy Test ID",
        icon: <ContentCopyIcon fontSize="small" />,
        action: () => {
          copyToClipboard(params.testId!, "Test ID");
        },
        keywords: ["copy", "test", "id"],
        group: "Copy",
      });
    }

    if (params.testSuiteId) {
      commands.push({
        id: "copy-suite-id",
        label: "Copy Suite ID",
        icon: <ContentCopyIcon fontSize="small" />,
        action: () => {
          copyToClipboard(params.testSuiteId!, "Suite ID");
        },
        keywords: ["copy", "suite", "id"],
        group: "Copy",
      });
    }

    if (params.runId) {
      commands.push({
        id: "copy-run-id",
        label: "Copy Run ID",
        icon: <ContentCopyIcon fontSize="small" />,
        action: () => {
          copyToClipboard(params.runId!, "Run ID");
        },
        keywords: ["copy", "run", "id"],
        group: "Copy",
      });
    }

    if (params.issueId) {
      commands.push({
        id: "copy-issue-id",
        label: "Copy Issue ID",
        icon: <ContentCopyIcon fontSize="small" />,
        action: () => {
          copyToClipboard(params.issueId!, "Issue ID");
        },
        keywords: ["copy", "issue", "id"],
        group: "Copy",
      });
    }

    return commands;
  }, [params, copyToClipboard]);

  // Filter static commands based on search
  const navigationCommands = useMemo(() => {
    if (!search) return allNavigationCommands;
    const searchLower = search.toLowerCase();
    return allNavigationCommands.filter((cmd) => {
      const matchesLabel = cmd.label.toLowerCase().includes(searchLower);
      const matchesKeywords = cmd.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower),
      );
      return matchesLabel || matchesKeywords;
    });
  }, [allNavigationCommands, search]);

  const createCommands = useMemo(() => {
    if (!search) return allCreateCommands;
    const searchLower = search.toLowerCase();
    return allCreateCommands.filter((cmd) => {
      const matchesLabel = cmd.label.toLowerCase().includes(searchLower);
      const matchesKeywords = cmd.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower),
      );
      return matchesLabel || matchesKeywords;
    });
  }, [allCreateCommands, search]);

  const copyCommands = useMemo(() => {
    if (!search) return allCopyCommands;
    const searchLower = search.toLowerCase();
    return allCopyCommands.filter((cmd) => {
      const matchesLabel = cmd.label.toLowerCase().includes(searchLower);
      const matchesKeywords = cmd.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower),
      );
      return matchesLabel || matchesKeywords;
    });
  }, [allCopyCommands, search]);

  // Dynamic search commands - separated by type for loading states
  const testCommands: CommandItem[] = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) return [];
    if (!tests || tests.length === 0) return [];

    return tests.map((test: Test) => ({
      id: `test-${test.id}`,
      label: test.name || "Unnamed Test",
      description: `Test • ${test.app_config?.name || "No app config"}`,
      icon: <ScienceIcon fontSize="small" />,
      action: () => {
        navigate(`/tests-main/tests/${test.id}`);
        setOpen(false);
      },
      group: "Tests",
    }));
  }, [tests, debouncedSearch, navigate]);

  const suiteCommands: CommandItem[] = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) return [];
    if (!suites || suites.length === 0) return [];

    return suites.slice(0, 5).map((suite: TestSuite) => ({
      id: `suite-${suite.id}`,
      label: suite.name || "Unnamed Suite",
      description: `Suite • ${suite.test_ids?.length || 0} tests`,
      icon: <FolderCopyIcon fontSize="small" />,
      action: () => {
        navigate(`/tests-main/test-suites/${suite.id}`);
        setOpen(false);
      },
      group: "Suites",
    }));
  }, [suites, debouncedSearch, navigate]);

  const issueCommands: CommandItem[] = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) return [];
    if (!issues || issues.length === 0) return [];

    const searchLower = debouncedSearch.toLowerCase();
    return issues
      .filter(
        (issue: Issue) =>
          issue.title?.toLowerCase().includes(searchLower) ||
          issue.slug?.toLowerCase().includes(searchLower),
      )
      .slice(0, 5)
      .map((issue: Issue) => ({
        id: `issue-${issue.id}`,
        label: issue.title || "Unnamed Issue",
        description: `Issue • ${issue.severity || "unknown"}`,
        icon: <BugReportIcon fontSize="small" />,
        action: () => {
          navigate(`/issues/${issue.id}`);
          setOpen(false);
        },
        group: "Issues",
      }));
  }, [issues, debouncedSearch, navigate]);

  // Group commands
  const groupedCommands = useMemo(() => {
    const allCommands = [
      ...navigationCommands,
      ...createCommands,
      ...copyCommands,
      ...testCommands,
      ...suiteCommands,
      ...issueCommands,
    ];

    const groups: Record<string, CommandItem[]> = {};
    allCommands.forEach((cmd) => {
      if (!groups[cmd.group]) {
        groups[cmd.group] = [];
      }
      groups[cmd.group].push(cmd);
    });
    return groups;
  }, [
    navigationCommands,
    createCommands,
    copyCommands,
    testCommands,
    suiteCommands,
    issueCommands,
  ]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "20vh",
      }}
      {...clickableProps(() => setOpen(false))}
      onClick={() => setOpen(false)}
    >
      <Command
        label="Command Menu"
        className="command-palette"
        onClick={(e) => e.stopPropagation()}
        shouldFilter={false}
      >
        <div className="command-input-wrapper">
          <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary, mr: 1 }} />
          <Command.Input
            ref={inputRef}
            placeholder="Type a command or search..."
            value={search}
            onValueChange={setSearch}
            className="command-input"
            autoFocus
          />
          <kbd className="command-kbd">{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}K</kbd>
        </div>

        <Command.List className="command-list">
          <Command.Empty className="command-empty">No results found.</Command.Empty>

          {Object.entries(groupedCommands).map(([group, commands]) => (
            <Command.Group key={group} heading={group} className="command-group">
              {group === "Tests" && testsLoading && debouncedSearch.length > 1 && (
                <Box
                  sx={{
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Searching tests...
                </Box>
              )}
              {group === "Suites" && suitesLoading && debouncedSearch.length > 1 && (
                <Box
                  sx={{
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Searching suites...
                </Box>
              )}
              {group === "Issues" && issuesLoading && debouncedSearch.length > 1 && (
                <Box
                  sx={{
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Searching issues...
                </Box>
              )}
              {commands.map((cmd) => (
                <Command.Item
                  key={cmd.id}
                  onSelect={cmd.action}
                  className="command-item"
                  keywords={cmd.keywords}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        color: theme.palette.primary.main,
                      }}
                    >
                      {cmd.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{cmd.label}</Box>
                      {cmd.description && (
                        <Box
                          sx={{
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cmd.description}
                        </Box>
                      )}
                    </Box>
                    {cmd.shortcut && <kbd className="command-kbd-small">{cmd.shortcut}</kbd>}
                  </Box>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </Box>
  );
};
